import { action, observable } from 'mobx';

import { linktrainrelay } from '@link/shinkansen/services';
import i18n from '@link/shinkansen/i18n';
import { getOrderLocaleLanguage } from '@link/shinkansen/utils';
import {
  BookingInfo,
  BookingSelection,
  CommonReq,
  Diagram,
  GetOrderParametersResp,
  SeatClass,
  SeatPreference,
  SeatPreferenceCode,
  Station,
  TimeRange
} from '@link/shinkansen/services/types';

export class Shinkansen {
  @observable accessor bookingId = '';
  @observable accessor token = '';
  /** Return URL supplied by the backend via the `t_url` query param on entry. */
  @observable accessor tUrl = '';
  @observable accessor bookingInfo: BookingInfo | null = null;
  /** Flat departure / arrival station lists from the API. */
  @observable accessor depStations: Station[] = [];
  @observable accessor arrStations: Station[] = [];
  /** Available trains (diagrams) for the current route. */
  @observable accessor diagrams: Diagram[] = [];
  /** Orderable departure-time windows from getStations (the time-range tabs). */
  @observable accessor orderableTimeRanges: TimeRange[] = [];
  @observable accessor orderParameters: GetOrderParametersResp | null = null;
  /** In-flight selection carried across the booking flow */
  @observable accessor selection: BookingSelection = {};

  @action
  setBookingId(bookingId: string) {
    this.bookingId = bookingId;
  }

  @action
  setToken(token: string) {
    this.token = token;
  }

  @action
  setTUrl(tUrl: string) {
    this.tUrl = tUrl;
  }

  /** Common request envelope (bookingId / token / locale) shared by all APIs. */
  private get common(): CommonReq {
    return {
      bookingId: this.bookingId || undefined,
      token: this.token || undefined,
      locale: { language: getOrderLocaleLanguage(i18n.language) }
    };
  }

  @action
  getBookingData = async (bookingId?: string) => {
    const orderNo = bookingId || this.bookingId;
    if (!orderNo) {
      return null;
    }
    // Switching to a different booking: drop stale in-flight state so the new
    // booking re-defaults its route and doesn't carry over old diagrams/ranges.
    if (this.bookingId && this.bookingId !== orderNo) {
      this.selection = {};
      this.diagrams = [];
      this.orderableTimeRanges = [];
    }
    // Keep bookingId in sync so `common` carries this order.
    this.bookingId = orderNo;
    const detail = await linktrainrelay.getOrderDetails({
      commonBookingId: orderNo,
      commonToken: this.token || undefined,
      commonLocaleLanguage: getOrderLocaleLanguage(i18n.language)
    });
    const bookingInfo = { ...detail, bookingId: orderNo };
    this.setBookingData(bookingInfo);

    // Stations ship inside the order detail; default the route to the first.
    const depStations = detail.depStations ?? [];
    const arrStations = detail.arrStations ?? [];
    this.setStationList(depStations, arrStations);
    if (
      !this.selection.fromStation &&
      depStations.length &&
      arrStations.length
    ) {
      this.selection = {
        ...this.selection,
        fromStation: depStations[0],
        toStation: arrStations[0]
      };
    }
    return bookingInfo;
  };

  /**
   * Orderable departure-time windows (time-range tabs). Reads raw JSON because
   * an older SDK resolves at runtime that strips every field but `stations`.
   */
  @action
  getStations = async () => {
    const response = await linktrainrelay.getStationsRaw({
      commonBookingId: this.bookingId || undefined,
      commonToken: this.token || undefined,
      commonLocaleLanguage: getOrderLocaleLanguage(i18n.language)
    });
    const json = (await response.raw.json()) as {
      orderable_time_ranges?: { from?: string; to?: string }[];
    };
    const ranges = (json.orderable_time_ranges ?? [])
      .filter((range): range is TimeRange => !!range.from && !!range.to)
      .map(range => ({ from: range.from, to: range.to }));
    this.setOrderableTimeRanges(ranges);
    return ranges;
  };

  @action
  searchDiagrams = async (range?: TimeRange) => {
    const { diagrams = [] } = await linktrainrelay.searchDiagrams({
      body: {
        common: this.common,
        depStationCode: this.selection.fromStation?.code,
        arrStationCode: this.selection.toStation?.code,
        depDate: this.bookingInfo?.departDate,
        depTimeFrom: range?.from,
        depTimeTo: range?.to
      }
    });
    this.setDiagrams(diagrams);
    return { diagrams };
  };

  @action
  submitBooking = () => {
    const { diagram, seatPreferenceCode } = this.selection;
    return linktrainrelay.createOrder({
      body: {
        common: this.common,
        diagramCode: diagram?.diagramCode,
        seatPreferenceCode
      }
    });
  };

  @action
  getOrderParameters = async () => {
    const { diagram, seatClass } = this.selection;
    const orderParameters = await linktrainrelay.getOrderParameters({
      body: {
        common: this.common,
        diagramCode: diagram?.diagramCode,
        seatClassId: seatClass?.seatClassId
      }
    });
    this.setOrderParameters(orderParameters);
    return orderParameters;
  };

  @action
  setBookingData(detail: BookingInfo) {
    this.bookingInfo = detail;
  }

  @action
  setStationList(depStations: Station[], arrStations: Station[]) {
    this.depStations = depStations;
    this.arrStations = arrStations;
  }

  @action
  setDiagrams(diagrams: Diagram[]) {
    this.diagrams = diagrams;
  }

  @action
  setOrderableTimeRanges(orderableTimeRanges: TimeRange[]) {
    this.orderableTimeRanges = orderableTimeRanges;
  }

  @action
  setOrderParameters(orderParameters: GetOrderParametersResp) {
    this.orderParameters = orderParameters;
  }

  @action
  setStations(fromStation: Station, toStation: Station) {
    this.selection = {
      ...this.selection,
      fromStation,
      toStation,
      diagram: undefined,
      seatClass: undefined,
      seatPreference: undefined,
      seatPreferenceCode: undefined
    };
    this.diagrams = [];
    this.orderParameters = null;
  }

  @action
  swapStations() {
    const { fromStation, toStation, routeReversed } = this.selection;
    this.selection = {
      ...this.selection,
      fromStation: toStation,
      toStation: fromStation,
      routeReversed: !routeReversed,
      diagram: undefined,
      seatClass: undefined,
      seatPreference: undefined,
      seatPreferenceCode: undefined
    };
    this.diagrams = [];
    this.orderParameters = null;
  }

  @action
  setDiagram(diagram: Diagram) {
    this.selection = {
      ...this.selection,
      diagram,
      seatClass: undefined,
      seatPreference: undefined,
      seatPreferenceCode: undefined
    };
    this.orderParameters = null;
  }

  @action
  setSeatClass(seatClass: SeatClass) {
    this.selection = { ...this.selection, seatClass };
  }

  @action
  setSeatPreferenceCode(seatPreferenceCode: SeatPreferenceCode) {
    this.selection = { ...this.selection, seatPreferenceCode };
  }

  @action
  setSeatPreference(seatPreference: SeatPreference) {
    this.selection = {
      ...this.selection,
      seatPreference,
      seatPreferenceCode: seatPreference.code
    };
  }

  @action
  resetSelection() {
    this.selection = {};
  }
}

export default new Shinkansen();
